/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React from "react";
import PropTypes from "prop-types";
import Row from "../../../common/core/Row";
import Col from "../../../common/core/Col";
import Dropdown from "../../../common/core/Dropdown";
import DropdownToggle from "../../../common/core/DropdownToggle";
import DropdownMenu from "../../../common/core/DropdownMenu";
import DropdownItem from "../../../common/core/DropdownItem";
import SearchSeason from "./SearchSeason";
import SeasonsData from "./SeasonsData";
import Pagination from "../../../common/components/Pagination";
import NoResult from "../../../common/components/NoResult";
import EditSeason from "./EditSeason";
import DeleteSeason from "./DeleteSeason";
import Alert from "../../../common/core/Alert";
import Permission from "../../../common/security/Permission";
import Rules from "../../../common/security/PermissionRules";

const Seasons = (props) => {
  const {
    seasonData,
    hasData,
    isLoading,
    page,
    getPageData,
    size,
    totalCount,
    addModal,
    toggleAddModal,
    handleCloseAddModal,
    season,
    handleChange,
    submitSeasonForm,
    handleSubmit,
    register,
    errors,
    editModal,
    toggleEditModal,
    submitEditSeasonForm,
    handleCloseEditModal,
    deteleModal,
    handleDeleteConfirmationModal,
    cancelConfirm,
    handleDeleteSeason,
    handleDateChange,
    searchTerm,
    handleSearchTerm,
    handleSearch,
    handleKeyPress,
    message,
    addSeasonInProgress,
    editSeasonInProgress,
    deleteSeasonInProgress,
    displaySize,
    toggleDropdown,
    dropdownOpen,
    handleSelectedSize,
  } = props;

  return (
    <div className="contents">
      <h3 className="fw-bold mb-3">Seasons</h3>
      <div className="search-outer mb-4">
        <SearchSeason
          addModal={addModal}
          toggleAddModal={toggleAddModal}
          handleCloseAddModal={handleCloseAddModal}
          season={season}
          handleChange={handleChange}
          submitSeasonForm={submitSeasonForm}
          handleSubmit={handleSubmit}
          register={register}
          errors={errors}
          handleDateChange={handleDateChange}
          searchTerm={searchTerm}
          handleSearchTerm={handleSearchTerm}
          handleSearch={handleSearch}
          handleKeyPress={handleKeyPress}
          message={message}
          addSeasonInProgress={addSeasonInProgress}
        />
      </div>
      {
        message && message.type === "success" && (
          <Alert color={message.type} className="mt-2">
            {message.message}
          </Alert>
        )
      }
      {deleteSeasonInProgress
        && (
          <>
            {
              message && message.type === "warning" && (
                <Alert color={message.type}>
                  {message.message}
                </Alert>
              )
            }
          </>
        )}
      <Permission
        allowed={Rules.Control.VIEW_SEASONS_LIST}
      >
        {hasData ? (
          <>
            <div className="data-outer">
              <Row className="data-options mb-4">
                <Col xs="12">
                  <div className="show-items">
                    {totalCount >= size && (
                      <>
                        Show
                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="dropdown-list mx-2">
                          <DropdownToggle caret color="white">
                            {size}
                          </DropdownToggle>
                          <DropdownMenu right>
                            {displaySize.map((display) => (
                              <>
                                {(display === 20 || totalCount >= display) && display && (
                                  <DropdownItem
                                    active={display === size}
                                    onClick={() => handleSelectedSize(display)}
                                  >
                                    {display}
                                  </DropdownItem>
                                )}
                              </>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                      </>
                    )}
                    Showing
                    <span className="mx-1">{totalCount}</span>
                    {totalCount > 1 ? "Results" : "Result"}
                  </div>
                </Col>
              </Row>
              <div className="data-list-outer">
                <div className="data-header-outer">
                  <div className="seasons-list-header">
                    <Row>
                      <Col>Season ID</Col>
                      <Col>Country ID</Col>
                      <Col xs="3">Season Name</Col>
                      <Col xs="2">Season Group</Col>
                      <Col>Start Date</Col>
                      <Col>End Date</Col>
                      <Col xs="1" className="justify-content-center">Actions</Col>
                    </Row>
                  </div>
                </div>
                <SeasonsData
                  seasons={seasonData}
                  isLoading={isLoading}
                  toggleEditModal={toggleEditModal}
                  handleDeleteConfirmationModal={handleDeleteConfirmationModal}
                />
                <EditSeason
                  season={season}
                  handleChange={handleChange}
                  editModal={editModal}
                  toggleEditModal={toggleEditModal}
                  submitEditSeasonForm={submitEditSeasonForm}
                  handleCloseEditModal={handleCloseEditModal}
                  handleSubmit={handleSubmit}
                  register={register}
                  errors={errors}
                  handleDateChange={handleDateChange}
                  message={message}
                  editSeasonInProgress={editSeasonInProgress}
                />
                <DeleteSeason
                  season={season}
                  deteleModal={deteleModal}
                  handleDeleteConfirmationModal={handleDeleteConfirmationModal}
                  cancelConfirm={cancelConfirm}
                  handleDeleteSeason={handleDeleteSeason}
                  deleteSeasonInProgress={deleteSeasonInProgress}
                  message={message}
                />
              </div>
              <div className="pagination-outer">
                <Pagination
                  selectedPage={page}
                  onPageChange={getPageData}
                  itemsPerPage={size}
                  totalCount={totalCount}
                />
              </div>
            </div>
          </>
        ) : (
          <NoResult
            isLoading={isLoading}
          />
        )}
      </Permission>
    </div>
  );
};

Seasons.propTypes = {
  seasonData: PropTypes.arrayOf(PropTypes.any).isRequired,
  hasData: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  getPageData: PropTypes.func.isRequired,
  size: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  addModal: PropTypes.bool.isRequired,
  toggleAddModal: PropTypes.func.isRequired,
  handleCloseAddModal: PropTypes.func.isRequired,
  season: PropTypes.objectOf(PropTypes.any).isRequired,
  handleChange: PropTypes.func.isRequired,
  submitSeasonForm: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.any).isRequired,
  editModal: PropTypes.bool.isRequired,
  toggleEditModal: PropTypes.func.isRequired,
  submitEditSeasonForm: PropTypes.func.isRequired,
  handleCloseEditModal: PropTypes.func.isRequired,
  deteleModal: PropTypes.bool.isRequired,
  handleDeleteConfirmationModal: PropTypes.func.isRequired,
  cancelConfirm: PropTypes.func.isRequired,
  handleDeleteSeason: PropTypes.func.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  handleSearchTerm: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleKeyPress: PropTypes.func.isRequired,
  message: PropTypes.objectOf(PropTypes.any).isRequired,
  addSeasonInProgress: PropTypes.bool.isRequired,
  editSeasonInProgress: PropTypes.bool.isRequired,
  deleteSeasonInProgress: PropTypes.bool.isRequired,
  displaySize: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleDropdown: PropTypes.func.isRequired,
  dropdownOpen: PropTypes.bool.isRequired,
  handleSelectedSize: PropTypes.func.isRequired,
};

export default Seasons;
